import React, { useEffect, useRef, useState, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';

const CallModal = ({ callState, onAccept, onDecline, onEndCall, localStream, remoteStream }) => {
  const { theme } = useContext(ThemeContext);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoDisabled, setVideoDisabled] = useState(false);

  useEffect(() => {
    let interval;
    if (callState.status === 'active') {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callState.status]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callState.status, videoDisabled]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, callState.status]);

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => track.enabled = !track.enabled);
      setAudioMuted(!audioMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream && callState.type === 'video') {
      localStream.getVideoTracks().forEach(track => track.enabled = !track.enabled);
      setVideoDisabled(!videoDisabled);
    }
  };

  const formatDuration = (totalSecs) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (callState.status === 'idle') return null;

  if (callState.status === 'incoming') {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className={`w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl border ${theme === 'dark' ? 'bg-[#151D30] border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center font-black text-white text-2xl uppercase shadow-md animate-pulse mb-4" style={{ backgroundColor: callState.callerAvatar?.startsWith('#') ? callState.callerAvatar : '#3B82F6' }}>
            {callState.callerName?.charAt(0)}
          </div>
          <h3 className="text-xl font-bold">{callState.callerName}</h3>
          <p className="text-gray-400 text-xs uppercase tracking-wider mt-1">Incoming {callState.type} call...</p>
          
          <div className="flex justify-center gap-6 mt-8">
            <button 
              onClick={onDecline}
              className="px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold text-sm rounded-xl shadow-md transition-transform transform hover:scale-105"
            >
              Decline
            </button>
            <button 
              onClick={onAccept}
              className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold text-sm rounded-xl shadow-md transition-transform transform hover:scale-105"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (callState.status === 'ringing') {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className={`w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl border ${theme === 'dark' ? 'bg-[#151D30] border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center font-black text-white text-2xl uppercase shadow-md animate-bounce mb-4" style={{ backgroundColor: '#3B82F6' }}>
            {callState.targetUser?.username?.charAt(0)}
          </div>
          <h3 className="text-xl font-bold">Ringing {callState.targetUser?.username}</h3>
          <p className="text-gray-400 text-xs uppercase tracking-wider mt-1">Establishing dynamic P2P negotiation matrix...</p>
          
          <button 
            onClick={onEndCall}
            className="mt-8 px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold text-sm rounded-xl shadow-md transition-colors"
          >
            Cancel Call
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50 animate-fade-in">
      <div className="absolute top-4 left-6 z-10 text-white bg-black/60 px-3 py-1.5 rounded-xl backdrop-blur-sm text-sm border border-white/10">
        <span className="font-semibold">{callState.targetUser?.username || callState.callerName}</span>
        <span className="mx-2 font-light text-gray-500">|</span>
        <span className="font-mono text-xs text-blue-400">{formatDuration(callDuration)}</span>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-gray-950">
        {callState.type === 'video' ? (
          <>
            {/* 
              FIX 1: Changed object-cover to object-contain to preserve aspect ratios 
              and prevent the viewport from zooming or cropping during track toggles.
            */}
            <video 
              ref={remoteVideoRef} 
              autoPlay 
              playsInline 
              className="w-full h-full object-contain"
            />
            
            {/* Local Stream Picture-in-Picture Box */}
            <div className="absolute top-4 right-4 w-28 h-40 sm:w-40 sm:h-56 bg-[#151D30] rounded-xl overflow-hidden border border-white/10 shadow-2xl z-10 flex items-center justify-center">
              {videoDisabled ? (
                <div className="flex flex-col items-center gap-1 p-2 text-center">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400 uppercase">
                    M
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium tracking-wide">Cam Off</span>
                </div>
              ) : (
                /* 
                  FIX 2: Ensured local feed uses object-cover inside an explicit 
                  aspect-locked card container to prevent distortion on re-initialization.
                */
                <video 
                  ref={localVideoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }} // Mirroring local preview
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center text-white">
            <div className="w-32 h-32 rounded-full bg-gradient-to-tr from-accentBlue to-accentViolet flex items-center justify-center text-4xl font-bold uppercase shadow-2xl mb-4 animate-pulse">
              {(callState.targetUser?.username || callState.callerName || 'U').charAt(0)}
            </div>
            <p className="text-sm text-gray-400 tracking-widest uppercase font-medium">Audio Transmission Active</p>
            {localStream && <audio ref={localVideoRef} autoPlay playsInline muted className="hidden" />}
            {remoteStream && <audio ref={remoteVideoRef} autoPlay playsInline className="hidden" />}
          </div>
        )}
      </div>

      <div className="h-24 bg-[#0B0F19] border-t border-gray-800/80 flex items-center justify-center gap-6 px-4 z-20">
        <button 
          onClick={toggleAudio}
          className={`p-3.5 rounded-xl transition-colors ${audioMuted ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          title={audioMuted ? "Unmute Microphone" : "Mute Microphone"}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {audioMuted ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            )}
          </svg>
        </button>

        {callState.type === 'video' && (
          <button 
            onClick={toggleVideo}
            className={`p-3.5 rounded-xl transition-colors ${videoDisabled ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
            title={videoDisabled ? "Enable Video Input" : "Disable Video Input"}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        )}

        <button 
          onClick={onEndCall}
          className="p-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 transition-transform transform active:scale-95"
          title="Terminate P2P Interface Instance"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8l2 2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CallModal;